/**
 * SuggestionDisplay component for showing and managing pending suggestions.
 * 
 * Features:
 * - Displays pending suggestions in a list or card format
 * - Shows original text with strikethrough
 * - Shows suggested text with highlight
 * - Accept/Reject buttons (author only)
 * - User info and timestamp
 * 
 * Called by:
 * - EssayDetailPage to show pending suggestions
 */
import { useState, useCallback } from 'react';
import {
  Card,
  Typography,
  Space,
  Avatar,
  Button,
  Tag,

  Empty,
  Popconfirm,
  message,
} from 'antd';
import {
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import type { SuggestionWithUser, User, Essay } from '@compendium/shared';
import { acceptSuggestion, rejectSuggestion } from '../services/api';

const { Text } = Typography;

// =============================================================================
// Props Interface
// =============================================================================

interface SuggestionDisplayProps {
  /** List of suggestions to display */
  suggestions: SuggestionWithUser[];
  /** Current authenticated user */
  currentUser: User | null;
  /** Callback when suggestions are modified */
  onSuggestionsChange: () => void;
  /** Callback when essay content is updated (after accepting) */
  onEssayUpdate?: (essay: Essay) => void;
  /** Whether to show as a compact list or full cards */
  compact?: boolean;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format a date string for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Get status color for tag.
 */
function getStatusColor(status: string): string {
  switch (status) {
    case 'pending':
      return 'processing';
    case 'accepted':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
}

// =============================================================================
// Single Suggestion Card Component
// =============================================================================

interface SuggestionCardProps {
  suggestion: SuggestionWithUser;
  currentUser: User | null;
  onSuggestionsChange: () => void;
  onEssayUpdate?: (essay: Essay) => void;
  compact?: boolean;
}

/**
 * Renders a single suggestion card with accept/reject actions.
 */
function SuggestionCard({
  suggestion,
  currentUser,
  onSuggestionsChange,
  onEssayUpdate,
  compact = false,
}: SuggestionCardProps) {
  const [isLoading, setIsLoading] = useState(false);

  // Check if user can accept/reject (author only)
  const canManage = currentUser?.role === 'author';
  const isPending = suggestion.status === 'pending';

  // Handle accept
  const handleAccept = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await acceptSuggestion(suggestion.id);
      message.success('Suggestion accepted and applied');
      onSuggestionsChange();
      onEssayUpdate?.(result.essay);
    } catch (error) {
      message.error('Failed to accept suggestion');
      console.error('Error accepting suggestion:', error);
    } finally {
      setIsLoading(false);
    }
  }, [suggestion.id, onSuggestionsChange, onEssayUpdate]);

  // Handle reject
  const handleReject = useCallback(async () => {
    setIsLoading(true);
    try {
      await rejectSuggestion(suggestion.id);
      message.success('Suggestion rejected');
      onSuggestionsChange();
    } catch (error) {
      message.error('Failed to reject suggestion');
      console.error('Error rejecting suggestion:', error);
    } finally {
      setIsLoading(false);
    }
  }, [suggestion.id, onSuggestionsChange]);

  if (compact) {
    // Compact list item view
    return (
      <div
        style={{
          padding: '12px',
          background: '#2d2d2d',
          borderRadius: '8px',
          marginBottom: '8px',
          border: '1px solid #3d3d3d',
        }}
      >
        {/* Header */}
        <Space size="small" style={{ marginBottom: '8px', width: '100%', justifyContent: 'space-between' }}>
          <Space size="small">
            <Avatar
              src={suggestion.user.avatarUrl}
              icon={!suggestion.user.avatarUrl && <UserOutlined />}
              size="small"
            />
            <Text strong style={{ color: '#fff', fontSize: '13px' }}>
              {suggestion.user.name}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {formatDate(suggestion.createdAt)}
            </Text>
          </Space>
          <Tag color={getStatusColor(suggestion.status)} style={{ margin: 0 }}>
            {suggestion.status}
          </Tag>
        </Space>

        {/* Text diff */}
        <div style={{ marginBottom: '8px' }}>
          <Space align="start" size="small">
            <Text
              delete
              type="danger"
              style={{ fontSize: '13px', maxWidth: '120px', display: 'inline-block' }}
              ellipsis
            >
              {suggestion.originalText}
            </Text>
            <SwapOutlined style={{ color: '#666' }} />
            <Text
              style={{
                color: '#52c41a',
                fontSize: '13px',
                maxWidth: '120px',
                display: 'inline-block',
              }}
              ellipsis
            >
              {suggestion.suggestedText}
            </Text>
          </Space>
        </div>

        {/* Actions */}
        {canManage && isPending && (
          <Space size="small">
            <Popconfirm
              title="Accept this suggestion?"
              description="The essay content will be updated."
              onConfirm={handleAccept}
              okText="Accept"
              cancelText="Cancel"
            >
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                loading={isLoading}
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
              >
                Accept
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Reject this suggestion?"
              onConfirm={handleReject}
              okText="Reject"
              cancelText="Cancel"
            >
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                loading={isLoading}
              >
                Reject
              </Button>
            </Popconfirm>
          </Space>
        )}
      </div>
    );
  }

  // Full card view
  return (
    <Card
      size="small"
      style={{
        marginBottom: '12px',
        background: '#2d2d2d',
        border: '1px solid #3d3d3d',
      }}
      styles={{ body: { padding: '16px' } }}
    >
      {/* Header */}
      <Space size="small" style={{ marginBottom: '12px', width: '100%', justifyContent: 'space-between' }}>
        <Space size="small">
          <Avatar
            src={suggestion.user.avatarUrl}
            icon={!suggestion.user.avatarUrl && <UserOutlined />}
            size="small"
          />
          <div>
            <Text strong style={{ color: '#fff', display: 'block' }}>
              {suggestion.user.name}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {formatDate(suggestion.createdAt)}
            </Text>
          </div>
        </Space>
        <Tag color={getStatusColor(suggestion.status)}>
          {suggestion.status.charAt(0).toUpperCase() + suggestion.status.slice(1)}
        </Tag>
      </Space>

      {/* Original text */}
      <div style={{ marginBottom: '12px' }}>
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
          Original:
        </Text>
        <div
          style={{
            padding: '8px 12px',
            background: 'rgba(255, 107, 107, 0.1)',
            borderRadius: '4px',
            borderLeft: '3px solid #ff6b6b',
          }}
        >
          <Text delete style={{ color: '#ff6b6b' }}>
            {suggestion.originalText}
          </Text>
        </div>
      </div>

      {/* Suggested text */}
      <div style={{ marginBottom: '12px' }}>
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
          Suggested:
        </Text>
        <div
          style={{
            padding: '8px 12px',
            background: 'rgba(82, 196, 26, 0.1)',
            borderRadius: '4px',
            borderLeft: '3px solid #52c41a',
          }}
        >
          <Text style={{ color: '#52c41a' }}>
            {suggestion.suggestedText}
          </Text>
        </div>
      </div>

      {/* Actions */}
      {canManage && isPending && (
        <Space size="small">
          <Popconfirm
            title="Accept this suggestion?"
            description="The essay content will be updated with the suggested text."
            onConfirm={handleAccept}
            okText="Accept"
            cancelText="Cancel"
          >
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={isLoading}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              Accept
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Reject this suggestion?"
            description="The suggestion will be marked as rejected."
            onConfirm={handleReject}
            okText="Reject"
            cancelText="Cancel"
          >
            <Button
              danger
              icon={<CloseOutlined />}
              loading={isLoading}
            >
              Reject
            </Button>
          </Popconfirm>
        </Space>
      )}

      {/* Resolved info */}
      {!isPending && suggestion.resolvedAt && (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {suggestion.status === 'accepted' ? 'Accepted' : 'Rejected'} {formatDate(suggestion.resolvedAt)}
        </Text>
      )}
    </Card>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * SuggestionDisplay component.
 * Displays a list of suggestions with accept/reject functionality.
 */
function SuggestionDisplay({
  suggestions,
  currentUser,
  onSuggestionsChange,
  onEssayUpdate,
  compact = false,
}: SuggestionDisplayProps) {
  // Filter to only pending suggestions
  const pendingSuggestions = suggestions.filter((s) => s.status === 'pending');

  if (pendingSuggestions.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Text type="secondary">
            No pending suggestions
          </Text>
        }
        style={{ padding: '24px' }}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <Space style={{ marginBottom: '12px' }}>
        <EditOutlined />
        <Text strong style={{ color: '#fff' }}>
          Pending Suggestions
        </Text>
        <Tag color="processing">{pendingSuggestions.length}</Tag>
      </Space>

      {/* Suggestions list */}
      {pendingSuggestions.map((suggestion) => (
        <SuggestionCard
          key={suggestion.id}
          suggestion={suggestion}
          currentUser={currentUser}
          onSuggestionsChange={onSuggestionsChange}
          onEssayUpdate={onEssayUpdate}
          compact={compact}
        />
      ))}
    </div>
  );
}

export default SuggestionDisplay;
